import React, { useState } from 'react';
import axios from 'axios';
import { fmt } from './ceGraphUtil';

// Множитель порога тейкера M_т модели порогов хеджа (ADR-088, поправка 2026-11-03 вечер) — правка прямо с вкладки «Граф». Чистый интерфейс к BFF
// POST /vector-clearing/hmodel-taker-mult: границы значений, предпросмотр применённого порога, шаг, интервал и решение «нужно подтверждение» считает бэкенд;
// здесь только ввод, показ ответа и подтверждение. Множитель отдаляет порог тейкера от порога мейкера: z_mkt' = min(потолок, max(z_lim, M_т·b_т)) считает ledger.
// Не путать с γ рядом (двигает оба порога) и с «γ skew» (сдвиг якоря по позиции).
const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api';
const URL = `${API_BASE}/vector-clearing/hmodel-taker-mult`;

const errText = (e) => {
  const d = e && e.response && e.response.data;
  return (d && (d.message || d.error)) || (e && e.message) || 'не выполнено';
};
const ago = (ms) => (ms == null ? '' : ms < 90000 ? `${Math.round(ms / 1000)} с назад` : ms < 5400000 ? `${Math.round(ms / 60000)} мин назад` : `${fmt(ms / 3600000, 1)} ч назад`);

const HmodelTakerMultControl = ({ info, onApplied }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);       // { kind: 'ok' | 'warn' | 'err', text }
  const [preview, setPreview] = useState(null);

  if (!info) return null;                     // старый BFF без блока
  const eff = info.effective;
  const cfg = info.configured;
  const unsupported = info.supported === false;
  const value = text.trim() === '' ? null : Number(text.replace(',', '.'));
  const valid = value !== null && Number.isFinite(value) && value > 0;

  const send = async (body, onOk) => {
    setBusy(true);
    const post = (b) => axios.post(URL, b, { timeout: 15000, headers: { 'Content-Type': 'application/json' } });
    try {
      onOk((await post(body)).data);
    } catch (e) {
      const d = e && e.response && e.response.data;
      if (d && d.error === 'taker_mult_confirmation_required') {
        // BFF назвал причины и выдал подписанный токен (минута, привязан к значению и причинам): подтверждение оператора повторяет запрос с ним
        if (d.confirm_token && window.confirm(`${d.message}\n\nПрименить всё равно?`)) {
          try { onOk((await post({ ...body, confirm_token: d.confirm_token })).data); }
          catch (e2) { setMsg({ kind: 'err', text: errText(e2) }); }
        } else setMsg({ kind: 'warn', text: d.confirm_token ? 'отменено' : d.message });
      } else setMsg({ kind: 'err', text: errText(e) });
    } finally { setBusy(false); }
  };

  const check = () => send({ taker_mult: value, dry_run: true }, (d) => {
    setPreview(d.preview || null);
    setMsg(d.needs_confirmation ? { kind: 'warn', text: d.message } : { kind: 'ok', text: 'проверка пройдена, можно применять' });
  });
  const apply = () => send({ taker_mult: value }, (d) => {
    setPreview(d.preview || null);
    setMsg({ kind: 'ok', text: d.unchanged ? 'то же значение — ничего не изменено' : `записано ×${fmt(d.configured, 2)}; ledger применит за ≈${d.applies_in_s} с, порог на агентах обновится сразу после этого` });
    if (onApplied) onApplied();
  });
  const toEnv = () => {
    if (!window.confirm('Вернуть значение окружения ledger (убрать значение оператора)?')) return;
    send({ taker_mult: null }, (d) => {
      setPreview(null);
      setMsg({ kind: 'ok', text: d.unchanged ? 'уже используется значение окружения' : 'значение окружения возвращено' });
      if (onApplied) onApplied();
    });
  };

  const hard = info.hard || null;
  const source = cfg !== null && cfg !== undefined ? 'задано оператором' : 'из окружения ledger';
  const last = info.lastChange;
  return (
    <div className="cg-gamma" data-testid="hmodel-taker-mult">
      <span className="cg-chip" title="Множитель порога тейкера M_т (ADR-088): порог тейкера в M_т раз дальше от порога мейкера. Больше M_т — шире мейкер-зона (хедж лимитной заявкой) и больше допустимый размах позиции до рыночного хеджа. Действует на переводчиков с моделью «on» и мейкер-зоной; у связок и без мейкер-зоны не применяется.">
        множитель порога тейкера:{' '}
        <b data-testid="taker-mult-effective">{eff ? `×${fmt(eff, 2)}` : '—'}</b>
        {eff ? <span className="cg-muted"> ({source}{info.inForce ? '' : '; не действует: модель не в режиме on или мейкер-зоны нет'})</span>
          : <span className="cg-muted"> {info.mixed ? '(у агентов разные значения — идёт схождение)' : info.effectiveSupported === false ? '(копия proto BFF старее ledger)' : '(модель выключена или ledger не отдаёт множитель)'}</span>}
        {eff && cfg !== null && cfg !== undefined && Math.abs(cfg - eff) > 1e-9 && <span className="cg-warn-t"> · записано ×{fmt(cfg, 2)}: ledger применит за ≈6 с или отверг значение</span>}
        {last && last.atMs ? <span className="cg-muted"> · последняя правка {ago(Date.now() - last.atMs)}</span> : null}
      </span>
      {unsupported ? (
        <span className="cg-chip cg-warn" role="status">колонка ce_hmodel_taker_mult не создана в БД — правка недоступна</span>
      ) : (
        <span className="cg-chip" role="group" aria-label="Изменить множитель порога тейкера">
          новый множитель{' '}
          <input type="text" inputMode="decimal" value={text} onChange={(ev) => setText(ev.target.value)} placeholder={hard ? `${hard[0]}…${hard[1]}` : '1…3'}
            className="cg-gamma-in" aria-label="Новый множитель порога тейкера" disabled={busy} />
          <button type="button" className="cg-seg" onClick={check} disabled={busy || !valid} title="Проверить значение и показать оценку порогов, ничего не записывая">Проверить</button>
          <button type="button" className="cg-seg" onClick={apply} disabled={busy || !valid} title="Записать в настройки: ledger подхватит за ≈6 с">Применить</button>
          <button type="button" className="cg-seg" onClick={toEnv} disabled={busy || cfg === null || cfg === undefined} title="Убрать значение оператора, вернуть значение окружения">К окружению</button>
        </span>
      )}
      {msg && <span className={`cg-chip ${msg.kind === 'err' ? 'cg-warn' : ''}`} role="status" data-testid="taker-mult-msg">{msg.text}</span>}
      {preview && preview.byAsset && (
        <span className="cg-chip" data-testid="taker-mult-preview" title="Оценка по сглаженным порогам агентов-переводчиков с мейкер-зоной: порог тейкера = max(порог мейкера, множитель · b_т). Потолок полосы не учтён — реальный порог может быть ниже.">
          порог тейкера, тыс. USDT, сейчас → после (без потолка):{' '}
          {Object.entries(preview.byAsset).map(([a, v]) => <span key={a}><b>{a}</b> {fmt(v.zmktNowK, 1)} → {fmt(v.zmktNewK, 1)}{' '}</span>)}
        </span>
      )}
    </div>
  );
};

export default HmodelTakerMultControl;
